import { omaSATOIdentityUtils } from '@sato/components';

import { createAsyncTask } from '@stores/taskStore';
import { isLocalEnv } from '@utils/featureFlags';

import { publicClient } from './client.api';
import { SIGNUP_API_URLS } from './login.api';

export type RefreshTokenResponse = {
  idToken: string;
  token: string;
  /** Token expiration time in seconds */
  expiresIn: number;
};

interface LoginResponse extends RefreshTokenResponse {
  refreshToken: string;
}

let refreshAuthTokenCall: Promise<RefreshTokenResponse> | null = null;

const refreshAuthTokens = async () => {
  const refreshToken = omaSATOIdentityUtils.getRefreshToken();

  if (!refreshToken) throw Error('Refresh token required');

  // If this exists, we are refreshing already
  if (!refreshAuthTokenCall) {
    refreshAuthTokenCall = renewAuthTokens(refreshToken).finally(
      () => (refreshAuthTokenCall = null)
    );
  }

  return refreshAuthTokenCall;
};

const renewAuthTokens = createAsyncTask(
  'renew-auth-tokens',
  async (refreshToken: string) => {
    // request axios directly without api utils to avoid circular dependencies in axios interceptors
    const response = await publicClient.post<RefreshTokenResponse>(
      SIGNUP_API_URLS.REFRESH_TOKEN,
      { refreshToken },
      { headers: { 'Auth-Type': 'awscognito' } }
    );

    return response.data;
  }
);

const signOut = createAsyncTask('signout', () =>
  publicClient
    .get(SIGNUP_API_URLS.SIGNOUT)
    // allow failing silently
    .catch(() => isLocalEnv && console.error('Logout request to API failed'))
);

const signIn = createAsyncTask('signin', (email: string, password: string) =>
  publicClient.post<LoginResponse>(
    SIGNUP_API_URLS.SIGNIN,
    {
      username: email,
      password,
    },
    { headers: { 'Auth-Type': 'awscognito' } }
  )
);

export const authApi = {
  signIn,
  signOut,
  refreshTokens: refreshAuthTokens,
};
